import { useEffect } from "react";
import {
  Home, LineChart, Briefcase, MessageCircle, Star, History,
  Bell, Calendar, BarChart3, Landmark, User, Activity,
  X, LogOut,
} from "lucide-react";

// Traço um pouco mais grosso que o padrão do Lucide (2px) — reforça a
// identidade de "terminal de trading" que os números em monospace do app
// já sugerem, em vez da linha fina que a maioria dos dashboards usa.
const ICON_STROKE = 2.25;

export const MENU_ITEMS = [
  { section: "PRINCIPAL", items: [
    { id: "home", label: "Home", icon: Home },
    { id: "dashboard", label: "Negociar", icon: LineChart },
    { id: "investimentos", label: "Investimentos", icon: Briefcase },
  ]},
  { section: "INTELIGÊNCIA ARTIFICIAL", items: [
    { id: "chat", label: "Bate-papo IA", icon: MessageCircle },
    { id: "score", label: "Pontuação", icon: Star },
    { id: "historico", label: "Histórico", icon: History },
    { id: "alertas", label: "Alertas", icon: Bell },
    { id: "relatorio", label: "Relatório", icon: Calendar },
  ]},
  { section: "FERRAMENTAS", items: [
    { id: "backtesting", label: "Teste retrospectivo", icon: BarChart3 },
    { id: "papertrading", label: "Paper Trading", icon: Landmark },
    { id: "perfil", label: "Meu Perfil", icon: User },
  ]},
  { section: "ADMIN", items: [
    { id: "saudesistema", label: "Painel de Saúde", icon: Activity, somenteAdmin: true },
  ]},
];

// Itens visíveis só pra quem é admin (day trade / paper trading)
const ITENS_ADMIN_APENAS = ["dashboard", "papertrading"];

export default function Sidebar({ open, onClose, page, setPage, onLogout, isAdmin, tema = "escuro" }) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const cores = tema === "claro"
    ? { bg: "#FFFFFF", border: "#E2E8F0", textPrimary: "#172033", textSecondary: "#64748B", itemBg: "#F4F7FA" }
    : { bg: "#0a0f1a", border: "#1e2d45", textPrimary: "#fff", textSecondary: "#999", itemBg: "#111a27" };

  // Filtro novo e isolado: só esconde itens marcados explicitamente com
  // somenteAdmin=true (hoje, só o Painel de Saúde). Não mexe no
  // ITENS_ADMIN_APENAS acima, que já existia e nunca foi ativado — deixado
  // como estava, pra não mudar o comportamento de Negociar/Paper Trading
  // sem isso ter sido pedido.
  const menuFiltrado = MENU_ITEMS
    .map(section => ({
      ...section,
      items: section.items.filter(item => !item.somenteAdmin || isAdmin),
    }))
    .filter(section => section.items.length > 0);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(2px)", zIndex: 998,
          opacity: open ? 1 : 0, visibility: open ? "visible" : "hidden",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Painel lateral */}
      <div style={{
        position: "fixed", top: 0, left: 0, height: "100vh", width: "280px", maxWidth: "82vw",
        background: cores.bg, borderRight: `1px solid ${cores.border}`, zIndex: 999,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.28s cubic-bezier(.4,0,.2,1)",
        display: "flex", flexDirection: "column",
        boxShadow: open ? "8px 0 40px rgba(0,0,0,0.5)" : "none",
      }}>
        {/* Header do menu */}
        <div style={{ padding: "20px 18px", borderBottom: `1px solid ${cores.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "32px", height: "32px", background: "linear-gradient(135deg,#00e5a0,#006eff)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>⚡</div>
            <div>
              <div style={{ fontWeight: "700", fontSize: "15px", color: cores.textPrimary }}>TRADE<span style={{ color: "#00e5a0" }}>AI</span></div>
              <div style={{ color: cores.textSecondary, fontSize: "9px", fontFamily: "monospace" }}>MENU PRINCIPAL</div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: cores.itemBg, border: `1px solid ${cores.border}`, color: cores.textSecondary, borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} strokeWidth={ICON_STROKE} />
          </button>
        </div>

        {/* Itens do menu */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 10px" }}>
          {menuFiltrado.map(section => (
            <div key={section.section} style={{ marginBottom: "18px" }}>
              <div style={{ color: cores.textSecondary, fontSize: "10px", fontFamily: "monospace", letterSpacing: "0.12em", padding: "0 10px", marginBottom: "8px" }}>
                {section.section}
              </div>
              {section.items.map(item => {
                const ativo = page === item.id;
                const Icone = item.icon;
                return (
                  <button key={item.id} className="sidebar-item" onClick={() => { setPage(item.id); onClose(); }}
                    style={{
                      width: "100%", display: "flex", alignItems: "center", gap: "12px",
                      background: ativo ? "#00e5a015" : "transparent",
                      border: ativo ? "1px solid #00e5a033" : "1px solid transparent",
                      color: ativo ? "#00e5a0" : cores.textSecondary,
                      borderRadius: "10px", padding: "11px 12px", marginBottom: "3px",
                      fontSize: "14px", fontWeight: ativo ? "700" : "500", cursor: "pointer",
                      fontFamily: "inherit", textAlign: "left", transition: "all 0.15s",
                    }}>
                    <span style={{ width: "22px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
                      <Icone size={18} strokeWidth={ICON_STROKE} />
                    </span>
                    {item.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 16px", borderTop: `1px solid ${cores.border}` }}>
          <button onClick={onLogout}
            style={{ width: "100%", background: "#ff4d6d15", border: "1px solid #ff4d6d33", color: "#ff4d6d", borderRadius: "10px", padding: "11px", fontSize: "13px", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <LogOut size={16} strokeWidth={ICON_STROKE} /> Sair da conta
          </button>
          <div style={{ color: cores.textSecondary, fontSize: "10px", fontFamily: "monospace", textAlign: "center", marginTop: "10px" }}>
            v2.0 · Dados: Brapi ⚡
          </div>
        </div>
      </div>
    </>
  );
}
